import React from "react"
import { Popover, PopoverContent, PopoverTrigger } from "~/react/ui/popover"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  HoverCardPortal,
} from "~/react/ui/hover-card"
import { Button, ButtonSuffix } from "~/react/ui/button"
import { Tag, TagIcon, TagLabel } from "~/react/ui/tag"
import { Icon } from "~/react/ui/icon"
import {
  DropdownBody,
  DropdownFooter,
  DropdownMenu,
} from "~/react/ui/dropdown-menu"
import {
  Alert,
  AlertIcon,
  AlertContent,
  AlertDescription,
} from "~/react/ui/alert"
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "~/react/ui/tooltip"
import { LeadSaveManagerListPicker } from "~/react/components/lead-save-manager"
import {
  Department,
  Type,
  departmentsList,
} from "~/react/components/search/filters"
import { CostCalculator } from "~/helpers/costCalculator"
import { Source, SourceType } from "~/react/components/search/source"
import { VerificationStatus } from "~/react/components/verification-status"
import { Checkbox } from "~/react/ui/checkbox"
import { leads_list_path } from "~/lib/routes"
import { CurrencyHelper } from "~/helpers/currency_helper"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"
import { MultiDomainSearchDomain } from "~/types/serializers"
import { LeadCampaignsManager } from "~/react/components/lead-campaigns-manager"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/react/ui/tabs"

const ResultListManager = ({
  emailId,
  lead,
  leadsLists,
  campaigns,
  handleLeadsListSave,
  handleCampaignsSave,
}) => {
  const [tabSelected, setTabSelected] = React.useState<"list" | "campaign">(
    "list",
  )
  const [isSaving, setIsSaving] = React.useState(false)
  const [listToUse, setListToUse] = React.useState<{
    name: string
    id: number | null
  } | null>(leadsLists[0])
  const [campaignToUse, setCampaignToUse] = React.useState<{
    name: string
    id: number | null
  } | null>(campaigns?.length > 0 ? campaigns[0] : null)
  const [isOpen, setIsOpen] = React.useState(false)

  const addLeadToList = async () => {
    setIsSaving(true)

    try {
      await handleLeadsListSave(listToUse.name, listToUse.id, emailId)
    } catch (error) {
      console.error("Failed to save lead to a list", error)
    } finally {
      setIsSaving(false)
    }
  }

  const addLeadToCampaign = async () => {
    setIsSaving(true)

    try {
      await handleCampaignsSave(campaignToUse.name, campaignToUse.id, emailId)
      setIsOpen(false)
    } catch (error) {
      console.error("Failed to save lead to a campaign", error)
    } finally {
      setIsSaving(false)
    }
  }
  return (
    <div className="lead-result__saved dropdown ml-auto">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          {lead ? (
            <Button variant="successLight" size="xs">
              {i18n.t("js.multi_domain_search.result.saved")}
              <span className="far fa-angle-down" aria-hidden="true"></span>
            </Button>
          ) : (
            <Button size="xs">
              {i18n.t("js.multi_domain_search.result.save")}
              <span className="far fa-angle-down" aria-hidden="true"></span>
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent align="end" className="lead-save-manager">
          <DropdownMenu>
            <Tabs onValueChange={setTabSelected}>
              <TabsList className="px-4">
                <TabsTrigger value="list">Add to a list</TabsTrigger>
                <TabsTrigger value="campaign">Add to a campaign</TabsTrigger>
              </TabsList>
              <TabsContent value="list">
                <DropdownBody>
                  {lead?.leads_list_ids?.length > 0 && (
                    <Alert size="small" className="mb-4">
                      <AlertIcon>
                        <span className="far fa-info-circle"></span>
                      </AlertIcon>
                      <AlertContent>
                        <AlertDescription>
                          {lead.leads_list_ids.length > 1 ? (
                            <div>
                              {i18n.t(
                                "js.multi_domain_search.result.result_list_manager.multiple.first",
                              ) + " "}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-decoration-underline">
                                    {lead.leads_list_ids.length}{" "}
                                    {i18n.t(
                                      "js.multi_domain_search.result.result_list_manager.multiple.lists",
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  {lead.leads_list_ids.map((listId) => (
                                    <div key={listId}>
                                      {
                                        leadsLists.find(
                                          (list) => list.id === listId,
                                        )?.name
                                      }
                                    </div>
                                  ))}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          ) : (
                            <div>
                              {i18n.t(
                                "js.multi_domain_search.result.result_list_manager.single.first",
                              )}
                              <a
                                className="text-decoration-underline"
                                href={leads_list_path(lead.leads_list_ids[0])}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                              >
                                {
                                  leadsLists.find(
                                    (list) =>
                                      list.id === lead.leads_list_ids[0],
                                  )?.name
                                }
                              </a>
                            </div>
                          )}
                        </AlertDescription>
                      </AlertContent>
                    </Alert>
                  )}
                  <LeadSaveManagerListPicker
                    id={emailId}
                    leadsLists={leadsLists}
                    setListToUse={setListToUse}
                    listToUse={listToUse}
                  />
                </DropdownBody>
              </TabsContent>
              <TabsContent value="campaign">
                <DropdownBody>
                  <LeadCampaignsManager
                    leadId={emailId}
                    campaigns={campaigns}
                    campaignToUse={campaignToUse}
                    setCampaignToUse={setCampaignToUse}
                  />
                </DropdownBody>
              </TabsContent>
            </Tabs>
          </DropdownMenu>
          <DropdownFooter>
            {tabSelected === "list" && (
              <Button
                variant="primary"
                size="sm"
                onClick={addLeadToList}
                disabled={isSaving}
              >
                {isSaving
                  ? i18n.t(
                      "js.multi_domain_search.result.result_list_manager.adding_to_list",
                    )
                  : i18n.t(
                      "js.multi_domain_search.result.result_list_manager.add_to_list",
                    )}
              </Button>
            )}
            {tabSelected === "campaign" && (
              <Button
                variant="primary"
                size="sm"
                onClick={addLeadToCampaign}
                disabled={isSaving}
              >
                {isSaving
                  ? i18n.t(
                      "js.multi_domain_search.result.result_campaign_manager.adding_to_campaign",
                    )
                  : i18n.t(
                      "js.multi_domain_search.result.result_campaign_manager.add_to_campaign",
                    )}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpen(false)
              }}
            >
              {i18n.t(
                "js.multi_domain_search.result.result_list_manager.dismiss",
              )}
            </Button>
          </DropdownFooter>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export type ResultType = {
  id: number
  name?: string | null
  email?: string | null
  jobTitle?: string | null
  companyLogo?: string | null
  companyName?: string | null
  phoneNumberExists: boolean
  fullNameExists: boolean
  domain?: string | null
  revealed: boolean
  lead?: {
    id: number
    leads_list_ids: number[]
  } | null
  department?: Department | "finance & accounting" | null
  type?: Type | null
  decisionMaker: boolean
  sources?: SourceType[]
  linkedinPresent?: boolean
  twitterPresent?: boolean
  linkedin?: string | null
  twitter?: string | null
  country?: string | null
  state?: string | null
  city?: string | null
  verification?: {
    date?: Date | null
    status?: string | null
  }
}
interface ResultProps {
  result: ResultType
  domain: MultiDomainSearchDomain | null
  selected: boolean
  hideCompanyDetails?: boolean
  leadsLists: { id: number; name: string }[]
  campaigns: { id: number; name: string }[]
  onSelect: (ids: number[]) => void
  handleReveal: (data: any) => void
  handleLeadsListSave: (name: string, listId: number, emailId: number) => void
  handleCampaignsSave: (
    name: string,
    campaignId: number,
    emailId: number,
  ) => void
  similarityThresholds?: {
    medium: number
    high: number
  }
  matchingKeywords?: string[]
  clickToCopy?: boolean
}

export const Result = ({
  result,
  isLast,
  domain: domainObject,
  selected,
  hideCompanyDetails,
  leadsLists,
  onSelect,
  handleReveal,
  handleLeadsListSave,
  handleCampaignsSave,
  campaigns,
  similarityThresholds,
  matchingKeywords,
  clickToCopy = false,
}: ResultProps) => {
  const isGeneric = result.type === "generic"
  // Similarity score helpers for lookalikes
  const getSimilarityScoreClass = (score?: number) => {
    if (!score) return ""
    const thresholds = similarityThresholds
    if (score >= thresholds.high) return "similarity-score--high"
    if (score >= thresholds.medium) return "similarity-score--mid"
    return "similarity-score--low"
  }

  const getSimilarityScoreLabel = (score?: number) => {
    if (!score) return ""
    const thresholds = similarityThresholds
    if (score >= thresholds.high)
      return i18n.t("js.lead_lookalikes.high_similarity")
    if (score >= thresholds.medium)
      return i18n.t("js.lead_lookalikes.medium_similarity")
    return i18n.t("js.lead_lookalikes.low_similarity")
  }
  const {
    id,
    name,
    jobTitle,
    companyLogo,
    companyName,
    domain,
    email,
    revealed,
    lead,
    sources,
    decisionMaker,
    verification,
    linkedinPresent,
    twitterPresent,
    linkedin,
    twitter,
    department,
  } = result
  const [displaySources, setDisplaySources] = React.useState(false)
  const [companyLogoFallback, setCompanyLogoFallback] = React.useState(false)
  const revealOperationCost = CostCalculator.calculateVisualRepresentation(
    "search",
    1,
  )
  const [isRevealing, setIsRevealing] = React.useState(false)
  const decisionMakerTooltip =
    name === null ? "Decision maker" : `${name} is a decision maker`
  let position = null
  if (!jobTitle && department) {
    const departmentLabel =
      department === "finance & accounting"
        ? "Finance"
        : departmentsList[department as Department]
    position = `${name ? "" : isGeneric ? "Generic email address ·" : "Person ·"} ${departmentLabel}`
  } else if (jobTitle) {
    position = `${jobTitle}`
  } else if (name && !jobTitle && !department) {
    position = ``
  } else {
    position = isGeneric ? "Generic email address" : "Person"
    if (!hideCompanyDetails) {
      position += " ·"
    }
  }

  return (
    <TooltipProvider>
      <article
        className={`lead-result ${isLast ? "lead-result--last" : ""}`}
        data-id={id}
        data-testid={`result-${id}`}
      >
        <Checkbox
          className="lead-result__checkbox"
          id={`lead-result-${id}`}
          checked={selected}
          onChange={() => onSelect([id])}
        />

        <div className="lead-result__primary">
          {name && (
            <label htmlFor={`lead-result-${id}`} className="lead-result__name">
              {name}
            </label>
          )}
          {decisionMaker && (
            <Icon
              icon="crown"
              size="sm"
              className="text-hint"
              data-controller="tooltip"
              aria-label={decisionMakerTooltip}
              data-bs-original-title={decisionMakerTooltip}
            />
          )}
          {(name || decisionMaker) && <hr className="lead-result__divider" />}
          <span
            className="lead-result__position"
            data-controller="tooltip"
            data-bs-original-title={jobTitle}
          >
            {position}
          </span>
          {position !== "" &&
            position !== null &&
            position !== "Person ·" &&
            position !== "Generic email address ·" &&
            !hideCompanyDetails && <>at</>}
          {!hideCompanyDetails && (
            <HoverCard>
              <HoverCardTrigger asChild>
                <span
                  className="lead-result-company"
                  data-testid="company-trigger"
                >
                  <img
                    className="lead-result-company__logo"
                    src={
                      companyLogoFallback ? defaultCompanyLogoUrl : companyLogo
                    }
                    onError={() => setCompanyLogoFallback(true)}
                  />
                  <span className="lead-result-company__name">
                    {companyName}
                  </span>
                </span>
              </HoverCardTrigger>
              <HoverCardPortal>
                <HoverCardContent asChild>
                  <article
                    className="company-id-card"
                    data-testid="company-id-card"
                  >
                    <header className="company-id-card__header">
                      <img
                        className="company-id-card__logo"
                        src={
                          companyLogoFallback
                            ? defaultCompanyLogoUrl
                            : companyLogo
                        }
                        onError={() => setCompanyLogoFallback(true)}
                      />
                      <div className="company-id-card__heading">
                        <strong
                          className="company-id-card__name"
                          data-testid="company-name-popover"
                        >
                          {domainObject?.company_name}
                        </strong>
                        <a
                          href={`https://${domainObject?.value}`}
                          target="_blank"
                          className="company-id-card__website"
                          data-testid="company-website-link"
                        >
                          {domainObject?.value}
                        </a>
                      </div>
                    </header>
                    <div className="company-id-card__tags">
                      <Tag>
                        <TagLabel>
                          {domainObject?.number_of_emails} email addresses
                        </TagLabel>
                      </Tag>
                      {/* <Tag>
                    <TagLabel>Hiring</TagLabel>
                  </Tag> */}
                    </div>
                    <hr className="h-separator m-0" />
                    {/* TODO(dev): Show the right values — only if available */}
                    <dl className="key-value key-value--grid">
                      {domainObject?.category && (
                        <div className="key-value__item">
                          <dt className="key-value__title">Industry</dt>
                          <dd className="key-value__desc">
                            {domainObject?.category}
                          </dd>
                        </div>
                      )}

                      {domainObject?.size && (
                        <div className="key-value__item">
                          <dt className="key-value__title">Size</dt>
                          <dd className="key-value__desc">
                            {domainObject?.size} employees
                          </dd>
                        </div>
                      )}

                      {domainObject?.address && (
                        <div className="key-value__item">
                          <dt className="key-value__title">Address</dt>
                          <dd className="key-value__desc">
                            {domainObject?.address}
                          </dd>
                        </div>
                      )}
                      {domainObject?.founded_year && (
                        <div className="key-value__item">
                          <dt className="key-value__title">Year founded</dt>
                          <dd className="key-value__desc">
                            {domainObject?.founded_year}
                          </dd>
                        </div>
                      )}
                      {domainObject?.company_type && (
                        <div className="key-value__item">
                          <dt className="key-value__title">Type</dt>
                          <dd className="key-value__desc">
                            {domainObject?.company_type}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </article>
                </HoverCardContent>
              </HoverCardPortal>
            </HoverCard>
          )}

          {/* Similarity Score for Lookalikes */}
          {result.similarity_score !== undefined && (
            <>
              <hr className="lead-result__divider" />
              <span
                className={`similarity-score ${getSimilarityScoreClass(result.similarity_score)}`}
                data-controller="tooltip"
                title={`${getSimilarityScoreLabel(result.similarity_score)} · ${Math.round(result.similarity_score)}%`}
              ></span>
            </>
          )}

          {/* Matching Keywords for Lookalikes */}
          {matchingKeywords && matchingKeywords.length > 0 && (
            <>
              <hr className="lead-result__divider" />
              <div className="lead-result__keywords">
                {matchingKeywords.length === 1 ? (
                  <Tag variant="lightBlue" size="xs">
                    <TagIcon>
                      <Icon icon="tag" />
                    </TagIcon>
                    <TagLabel>{matchingKeywords[0]}</TagLabel>
                  </Tag>
                ) : (
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Tag
                        variant="lightBlue"
                        size="xs"
                        className="cursor-pointer"
                      >
                        <TagIcon>
                          <Icon icon="tag" />
                        </TagIcon>
                        <TagLabel>
                          {matchingKeywords.length} matching keywords
                        </TagLabel>
                      </Tag>
                    </HoverCardTrigger>
                    <HoverCardPortal>
                      <HoverCardContent asChild>
                        <article className="company-id-card">
                          <header className="company-id-card__header">
                            <Icon icon="tag" className="text-hint" />
                            <div className="company-id-card__heading">
                              <strong className="company-id-card__name">
                                Matching keywords
                              </strong>
                            </div>
                          </header>
                          <div className="company-id-card__tags">
                            {matchingKeywords.map((keyword, idx) => (
                              <Tag key={idx} variant="light" size="xs">
                                <TagLabel>{keyword}</TagLabel>
                              </Tag>
                            ))}
                          </div>
                        </article>
                      </HoverCardContent>
                    </HoverCardPortal>
                  </HoverCard>
                )}
              </div>
            </>
          )}

          {revealed && (
            <ResultListManager
              emailId={id}
              lead={lead}
              leadsLists={leadsLists}
              handleLeadsListSave={handleLeadsListSave}
              handleCampaignsSave={handleCampaignsSave}
              campaigns={campaigns}
            />
          )}
        </div>

        <div className="lead-result__secondary">
          {revealed ? (
            clickToCopy ? (
              <span
                className="lead-result__email lead-result__email--revealed click-to-copy"
                title={i18n.t("js.multi_domain_search.result.click_to_copy")}
                data-controller="tooltip copy"
                data-copy-text-value={email}
                data-action="click->copy#copy"
              >
                {email}
              </span>
            ) : (
              <span className="lead-result__email lead-result__email--revealed">
                {email}
              </span>
            )
          ) : (
            <button
              className="lead-result__email"
              type="button"
              data-controller="tooltip"
              data-bs-html="true"
              title={`${i18n.t("js.multi_domain_search.result.reveal_email_address.first")} <div class='cost-indicator cost-indicator--suffix pl-1 ml-1'><span class='h-icon far h-icon--xs fa-coin cost-indicator__icon'></span>${CurrencyHelper.numberWithDelimiter(revealOperationCost)}</div>`}
              onClick={() => handleReveal(id)}
            >
              ********@{domain}
            </button>
          )}
          <VerificationStatus
            email={email}
            date={verification?.date || null}
            status={verification?.status}
            revealed={revealed}
          />
          {(linkedinPresent || twitterPresent) && (
            <hr className="lead-result__divider" />
          )}
          {revealed ? (
            <>
              {linkedinPresent && (
                <a
                  href={linkedin}
                  className="lead-result__social"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  <Icon icon="linkedin-in" variant="brand" size="sm" />
                </a>
              )}
              {twitterPresent && (
                <a
                  href={`https://x.com/${twitter}`}
                  className="lead-result__social"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  <Icon icon="x-twitter" variant="brand" size="sm" />
                </a>
              )}

              <button
                type="button"
                className="btn-empty ds-result__source mt-0 ml-auto"
                aria-label="Toggle sources"
                onClick={() => setDisplaySources(!displaySources)}
              >
                {sources.length > 20
                  ? i18n.t("js.multi_domain_search.result.sources", {
                      count: "20+",
                    })
                  : i18n.t("js.multi_domain_search.result.sources", {
                      count: sources.length,
                    })}
                {displaySources ? (
                  <span
                    className="far fa-angle-up"
                    data-toggle-display-target="hiddenIcon"
                  ></span>
                ) : (
                  <span
                    className="far fa-angle-down"
                    data-toggle-display-target="displayedIcon"
                  ></span>
                )}
              </button>
            </>
          ) : (
            <>
              {linkedinPresent && (
                <a
                  className="lead-result__social"
                  rel="noopener noreferrer nofollow"
                >
                  <Icon
                    icon="linkedin-in"
                    variant="brand"
                    size="sm"
                    className="text-hint"
                  />
                </a>
              )}
              {twitterPresent && (
                <a
                  className="lead-result__social"
                  rel="noopener noreferrer nofollow"
                >
                  <Icon
                    icon="x-twitter"
                    variant="brand"
                    size="sm"
                    className="text-hint"
                  />
                </a>
              )}
            </>
          )}
        </div>

        {revealed && displaySources && (
          <div
            className="ds-result__sources pl-0 mt-1"
            id={`people-sources-${id}`}
          >
            <ul className="ds-sources-list">
              {sources.map((source) => (
                <Source key={source.link} source={source} />
              ))}
            </ul>
          </div>
        )}

        {!revealed && (
          <div className="lead-result__actions">
            <Button
              size="xs"
              onClick={() => {
                handleReveal(id)
                setIsRevealing(true)
              }}
              disabled={!revealed && isRevealing}
            >
              {isRevealing
                ? i18n.t("js.multi_domain_search.result.revealing")
                : i18n.t("js.multi_domain_search.result.reveal")}
              <ButtonSuffix>
                <span className="cost-indicator cost-indicator--bare">
                  <Icon
                    icon="coin"
                    size="xs"
                    className="cost-indicator__icon"
                  />
                  {CurrencyHelper.numberWithDelimiter(revealOperationCost)}
                </span>
              </ButtonSuffix>
            </Button>
          </div>
        )}
      </article>
    </TooltipProvider>
  )
}
